import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, User, Scale } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { Link } from "react-router-dom";
import LandingQuestionPrompt from "./landing/LandingQuestionPrompt";

const QUESTION_TYPING_MS = 28;
const ANSWER_TYPING_MS = 38;
const BADGE_DELAY_MS = 400;
const THINKING_DURATION_MS = 1400;

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = () => setReduced(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return reduced;
}

function HeroMockupCard({
  t,
  dir,
  reducedMotion,
  phase,
  questionFull,
  answerFull,
  qLen,
  aLen,
  showQuestion,
  showThinking,
  showAnswerLabel,
  showAnswerBody,
  showRefs,
}) {
  return (
    <div
      className="hero-mockup-card relative w-full rounded-2xl p-5 md:p-6"
      style={{
        backgroundColor: "var(--landing-surface)",
        border: "1px solid var(--landing-border)",
        boxShadow: "var(--landing-shadow-lg)",
      }}
    >
      <motion.div
        initial={reducedMotion ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium mb-4"
        style={{
          backgroundColor: "var(--landing-surface-alt)",
          color: "var(--landing-primary)",
          border: "1px solid var(--landing-border)",
        }}
      >
        <Scale className="h-3.5 w-3.5" />
        {t("hero.mockup.badge")}
      </motion.div>

      <AnimatePresence>
        {showQuestion && (
          <motion.div
            initial={reducedMotion ? false : { opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="mb-3"
          >
            <p
              className="text-xs font-semibold mb-1.5 flex items-center gap-1.5"
              style={{ color: "var(--landing-text-secondary)" }}
            >
              <User className="h-3.5 w-3.5" />
              {t("hero.mockup.questionLabel")}
            </p>
            <div
              className="rounded-xl px-3.5 py-2.5 text-sm leading-relaxed text-start"
              style={{
                backgroundColor: "var(--landing-surface-alt)",
                border: "1px solid var(--landing-border)",
                color: "var(--landing-text)",
              }}
            >
              {questionFull.slice(0, qLen)}
              {phase === "question" && (
                <span
                  className="inline-block w-0.5 h-4 align-middle ms-0.5 animate-pulse"
                  style={{
                    backgroundColor: "var(--landing-primary)",
                    verticalAlign: "text-bottom",
                  }}
                />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showThinking && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="mb-3 flex items-center gap-1.5 ps-1"
            style={{ color: "var(--landing-primary)" }}
          >
            <span className="flex gap-1">
              {[0, 1, 2].map((i) => (
                <motion.span
                  key={i}
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: "var(--landing-primary)" }}
                  animate={{ opacity: [0.4, 1, 0.4] }}
                  transition={{
                    duration: 1,
                    repeat: Infinity,
                    delay: i * 0.2,
                  }}
                />
              ))}
            </span>
            <span className="text-xs font-medium opacity-80">
              {dir === "rtl" ? "جاري التحليل..." : "Analyzing..."}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {(showAnswerLabel || showAnswerBody) && (
          <motion.div
            initial={reducedMotion ? false : { opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="mb-4"
          >
            <p
              className="text-xs font-semibold mb-1.5 flex items-center gap-1.5"
              style={{ color: "var(--landing-primary)" }}
            >
              <Scale className="h-3.5 w-3.5" />
              {t("hero.mockup.answerLabel")}
            </p>
            <div
              className="rounded-xl px-3.5 py-2.5 text-sm leading-relaxed text-start"
              style={{
                backgroundColor: "var(--landing-surface)",
                border: "1px solid var(--landing-primary)",
                color: "var(--landing-text)",
                boxShadow: "var(--landing-shadow)",
              }}
            >
              {answerFull.slice(0, aLen)}
              {phase === "answer" && (
                <span
                  className="inline-block w-0.5 h-4 align-middle ms-0.5 animate-pulse"
                  style={{
                    backgroundColor: "var(--landing-primary)",
                    verticalAlign: "text-bottom",
                  }}
                />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showRefs && (
          <motion.div
            initial={reducedMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <p
              className="text-xs font-semibold mb-2 text-start"
              style={{ color: "var(--landing-text-secondary)" }}
            >
              {t("hero.mockup.refsTitle")}
            </p>
            <div className="flex flex-wrap gap-1.5 mb-4 justify-center md:justify-start">
              {["hero.mockup.ref1", "hero.mockup.ref2", "hero.mockup.ref3"].map((key) => (
                <span
                  key={key}
                  className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium"
                  style={{
                    backgroundColor: "var(--landing-surface-alt)",
                    color: "var(--landing-primary)",
                    border: "1px solid var(--landing-border)",
                  }}
                >
                  {t(key)}
                </span>
              ))}
            </div>

            <div
              className="pt-3 border-t border-[var(--landing-border)] flex items-center justify-center gap-1.5 text-xs font-medium"
              style={{ color: "var(--landing-text-secondary)" }}
            >
              <Scale className="h-3.5 w-3.5" style={{ color: "var(--landing-primary)" }} />
              {t("hero.mockup.footer")}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Hero() {
  const { t } = useTranslation();
  const { dir } = useLanguage();
  const isRtl = dir === "rtl";
  const reducedMotion = useReducedMotion();

  const questionFull = t("hero.mockup.questionText");
  const answerFull = t("hero.mockup.answerText");

  const [phase, setPhase] = useState("badge");
  const [qLen, setQLen] = useState(0);
  const [aLen, setALen] = useState(0);

  useEffect(() => {
    setPhase("badge");
    setQLen(0);
    setALen(0);
  }, [questionFull, answerFull]);

  useEffect(() => {
    if (reducedMotion) {
      const timer = setTimeout(() => {
        setPhase("done");
        setQLen(questionFull.length);
        setALen(answerFull.length);
      }, 300);
      return () => clearTimeout(timer);
    }
    const timer = setTimeout(() => setPhase("question"), BADGE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [reducedMotion, questionFull.length, answerFull.length]);

  useEffect(() => {
    if (reducedMotion || phase !== "question") return;
    if (qLen >= questionFull.length) {
      setPhase("thinking");
      return;
    }
    const id = setInterval(() => {
      setQLen((prev) => {
        if (prev >= questionFull.length) {
          setPhase("thinking");
          return questionFull.length;
        }
        return prev + 1;
      });
    }, QUESTION_TYPING_MS);
    return () => clearInterval(id);
  }, [reducedMotion, phase, qLen, questionFull]);

  useEffect(() => {
    if (phase !== "thinking") return;
    const id = setTimeout(() => setPhase("answer"), THINKING_DURATION_MS);
    return () => clearTimeout(id);
  }, [phase]);

  useEffect(() => {
    if (phase !== "answer") return;
    if (aLen >= answerFull.length) {
      setPhase("done");
      return;
    }
    const id = setInterval(() => {
      setALen((prev) => {
        if (prev >= answerFull.length) {
          setPhase("done");
          return answerFull.length;
        }
        return prev + 1;
      });
    }, ANSWER_TYPING_MS);
    return () => clearInterval(id);
  }, [phase, aLen, answerFull]);

  const showQuestion = phase === "question" || phase === "thinking" || phase === "answer" || phase === "done";
  const showThinking = phase === "thinking";
  const showAnswerLabel = phase === "answer" || phase === "done";
  const showAnswerBody = phase === "answer" || phase === "done";
  const showRefs = phase === "done";

  return (
    <section
      className="hero-centered-section relative pt-24 pb-16 md:pt-28 md:pb-20 overflow-hidden"
      style={{ backgroundColor: "var(--landing-bg)" }}
    >
      <div
        className="absolute inset-0 opacity-[0.4] pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(to right, var(--landing-border) 1px, transparent 1px),
            linear-gradient(to bottom, var(--landing-border) 1px, transparent 1px)
          `,
          backgroundSize: "64px 64px",
        }}
      />
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(circle, var(--landing-surface-alt) 0%, transparent 70%)",
          opacity: 0.8,
        }}
      />

      <div className="container mx-auto px-4 relative z-10">
        <div className="hero-centered-stack mx-auto w-full max-w-3xl flex flex-col items-center text-center" dir={dir}>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="hero-centered-intro w-full flex flex-col items-center"
          >
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium mb-6"
              style={{
                backgroundColor: "var(--landing-surface-alt)",
                color: "var(--landing-primary)",
                border: "1px solid var(--landing-border)",
              }}
            >
              <span className="relative flex h-2 w-2">
                <span
                  className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                  style={{ backgroundColor: "var(--landing-accent)" }}
                />
                <span
                  className="relative inline-flex rounded-full h-2 w-2"
                  style={{ backgroundColor: "var(--landing-primary)" }}
                />
              </span>
              {t("brand.name")}
            </div>

            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 max-w-3xl"
              style={{ color: "var(--landing-text)" }}
            >
              {t("hero.title")}
            </h1>

            <div
              className="text-lg md:text-xl mb-8 max-w-2xl mx-auto leading-relaxed space-y-1.5"
              style={{ color: "var(--landing-text-secondary)" }}
            >
              <p className="m-0">{t("hero.descriptionLine1")}</p>
              <p className="m-0">{t("hero.descriptionLine2")}</p>
              <p className="m-0">{t("hero.descriptionLine3")}</p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 justify-center mb-10 md:mb-12">
              <Link to="/register">
                <Button size="lg" className="landing-btn-primary text-base h-12 px-8 w-full sm:w-auto">
                  {t("hero.cta")}
                  {isRtl ? (
                    <ArrowLeft className="ml-2 h-5 w-5" />
                  ) : (
                    <ArrowRight className="mr-2 h-5 w-5" />
                  )}
                </Button>
              </Link>
              <Link to="/login">
                <Button
                  variant="outline"
                  size="lg"
                  className="landing-btn-outline text-base h-12 px-8 w-full sm:w-auto"
                >
                  {t("hero.login")}
                </Button>
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="hero-mockup-wrap w-full mb-6 md:mb-8"
          >
            <HeroMockupCard
              t={t}
              dir={dir}
              reducedMotion={reducedMotion}
              phase={phase}
              questionFull={questionFull}
              answerFull={answerFull}
              qLen={qLen}
              aLen={aLen}
              showQuestion={showQuestion}
              showThinking={showThinking}
              showAnswerLabel={showAnswerLabel}
              showAnswerBody={showAnswerBody}
              showRefs={showRefs}
            />
          </motion.div>

          <LandingQuestionPrompt />
        </div>
      </div>
    </section>
  );
}
